import React, { useState, useMemo } from 'react';
import { Search, X, Palette } from 'lucide-react';
import { ColorPickerPanel } from 'tdesign-react';
import { EMOJI_ICONS, emojiLabels } from '../../../components/lucideIcons';
import { presetColors, getOrgLabel } from '../constants';
import { tokenStorage } from '../../../api';

// ============ 机构称谓 Hook ============
/** 企业与政务单位共用同一套预览组件，称谓按机构类型返回「企业」/「单位」 */
export const useOrgLabel = (): string => {
  const tenantInfo = tokenStorage.getTenantInfo();
  return getOrgLabel(tenantInfo?.orgType, tenantInfo?.tenantType);
};

// ============ DynamicIcon 组件 ============
const EMOJI_VALUES = new Set(Object.values(EMOJI_ICONS));

interface DynamicIconProps {
  iconName: string;
  className?: string;
}

export const DynamicIcon = ({ iconName, className }: DynamicIconProps) => {
  if (EMOJI_VALUES.has(iconName)) {
    return <span className={`${className || 'w-4 h-4'} inline-flex items-center justify-center text-base`}>{iconName}</span>;
  }

  const emojiIcon = (EMOJI_ICONS as Record<string, string>)[iconName];
  if (emojiIcon) {
    return <span className={`${className || 'w-4 h-4'} inline-flex items-center justify-center text-base`}>{emojiIcon}</span>;
  }

  return <div className={className || 'w-4 h-4 bg-gray-300 rounded'} />;
};

// ============ IconPicker 组件 ============
interface IconPickerProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
}

export const IconPicker = ({ value, onChange, label }: IconPickerProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [searchText, setSearchText] = useState('');

  const filteredIcons = useMemo(() => {
    const emojiEntries = Object.entries(EMOJI_ICONS);
    if (!searchText.trim()) {
      return emojiEntries;
    }
    const lowerSearch = searchText.toLowerCase();
    return emojiEntries.filter(([key]) =>
      key.toLowerCase().includes(lowerSearch) ||
      (emojiLabels[key] && emojiLabels[key].includes(searchText))
    );
  }, [searchText]);

  const getSelectedLabel = () => {
    const emojiEntry = Object.entries(EMOJI_ICONS).find(([, v]) => v === value);
    if (emojiEntry) {
      return emojiLabels[emojiEntry[0]] || emojiEntry[0];
    }
    return value;
  };

  return (
    <div className="space-y-2">
      {label && <label className="text-sm text-gray-600">{label}</label>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="w-10 h-10 rounded-lg border border-gray-300 bg-white flex items-center justify-center hover:border-[#1c5bff] hover:bg-blue-50 transition-colors"
        >
          {value ? <DynamicIcon iconName={value} className="w-5 h-5" /> : (
            <Palette className="w-4 h-4 text-gray-400" />
          )}
        </button>
        {value && (
          <span className="text-xs text-gray-500 font-medium">
            {getSelectedLabel()}
          </span>
        )}
      </div>

      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowPicker(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-[520px] max-h-[560px] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-800">选择图标</h3>
              <button onClick={() => setShowPicker(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-hidden">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="搜索 emoji 名称（如 building, phone）..."
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#1c5bff] focus:ring-1 focus:ring-[#1c5bff]"
                />
              </div>
              <div className="grid grid-cols-8 gap-2 overflow-y-auto max-h-[420px]">
                {filteredIcons.map(([key, val]) => {
                  const isSelected = value === val;
                  const iconLabel = emojiLabels[key] || key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        onChange(val);
                        setShowPicker(false);
                      }}
                      title={iconLabel}
                      className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-[#1c5bff] bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">{val}</span>
                      <span className="text-[10px] text-gray-500 mt-1 truncate w-full text-center">
                        {iconLabel}
                      </span>
                    </button>
                  );
                })}
                {filteredIcons.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-400 text-sm">
                    未找到匹配的图标
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ ColorPickerField 组件 ============
interface ColorPickerFieldProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
}

export const ColorPickerField = ({ value, onChange, label }: ColorPickerFieldProps) => {
  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm text-gray-600">{label}</label>
          {value && (
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: value }} />
              <span className="text-xs font-mono font-medium text-gray-700">{value.toUpperCase()}</span>
            </div>
          )}
        </div>
      )}
      <ColorPickerPanel
        value={value}
        onChange={(val: string) => onChange(val)}
        format="HEX"
        clearable
        borderless
        swatchColors={presetColors}
        showPrimaryColorPreview
      />
    </div>
  );
};
